import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState, StatusBadge } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// ── Vote animation keyframes
function injectVoteKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ur-vote-kf')) return;
  const s = document.createElement('style');
  s.id = 'ur-vote-kf';
  s.textContent = `
    @keyframes votePop {
      0%   { transform: scale(1); }
      30%  { transform: scale(1.4) rotate(-8deg); }
      55%  { transform: scale(0.85) rotate(4deg); }
      75%  { transform: scale(1.12) rotate(-2deg); }
      100% { transform: scale(1) rotate(0deg); }
    }
    @keyframes voteRipple {
      0%   { transform: scale(0.6); opacity: 0.8; }
      100% { transform: scale(2.8); opacity: 0; }
    }
    @keyframes votePlusOne {
      0%   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
      60%  { opacity: 1; transform: translateX(-50%) translateY(-20px) scale(1.15); }
      100% { opacity: 0; transform: translateX(-50%) translateY(-36px) scale(0.8); }
    }
    @keyframes voteUndo {
      0%   { transform: scale(1); }
      40%  { transform: scale(0.72); }
      100% { transform: scale(1); }
    }
  `;
  document.head.appendChild(s);
}
injectVoteKeyframes();

// ── Live countdown timer for complaint cards
function CardTimer({ deadline, label }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  if (!deadline) return null;
  const diff = new Date(deadline && !deadline.endsWith('Z') ? deadline + 'Z' : deadline).getTime() - now;
  const isOverdue = diff < 0;
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const timeStr = h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  const color = isOverdue ? '#ef4444' : diff < 3600000 ? '#f59e0b' : '#10b981';
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 6,
      background: isOverdue ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.06)',
      border: `1px solid ${color}44`, marginTop: 6 }}>
      <span style={{ fontSize: 11 }}>{isOverdue ? '\u26a0\ufe0f' : '\u23f1'}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
        {label}: {isOverdue ? `+${timeStr}` : timeStr}
      </span>
    </div>
  );
}

// ── Star rating
function StarRating({ value, onChange, readonly }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n}
          onClick={() => !readonly && onChange && onChange(n)}
          onMouseEnter={() => !readonly && setHovered(n)}
          onMouseLeave={() => !readonly && setHovered(0)}
          style={{ fontSize: 22, cursor: readonly ? 'default' : 'pointer',
            color: n <= (hovered || value) ? '#f59e0b' : 'rgba(255,255,255,0.2)',
            transition: 'color 0.15s' }}>
          &#9733;
        </span>
      ))}
    </div>
  );
}

// ── Feedback modal
function FeedbackModal({ complaint, onClose, onSubmitted }) {
  const [rating, setRating] = useState(complaint.feedback_rating || 0);
  const [comment, setComment] = useState(complaint.feedback_comment || '');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const alreadyDone = !!complaint.feedback_rating;

  const submit = async () => {
    if (!rating) { setErr('Please select a rating'); return; }
    setLoading(true);
    try {
      await api.post(`/community/feedback/${complaint.id}`, { rating, comment });
      onSubmitted(complaint.id, rating, comment);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to submit feedback');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}>
      <div style={{ background: 'linear-gradient(145deg,#1e1b4b,#1a1a2e)',
        border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 420 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
          {alreadyDone ? 'Your Feedback' : 'Rate Resolution'}
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 20 }}>{complaint.title}</div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>Rating</div>
          <StarRating value={rating} onChange={alreadyDone ? null : setRating} readonly={alreadyDone} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>Comment (optional)</div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} disabled={alreadyDone}
            placeholder="How was the resolution handled?" rows={3}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, resize: 'vertical',
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box',
              opacity: alreadyDone ? 0.6 : 1 }} />
        </div>
        {err && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9,
            border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#fff',
            cursor: 'pointer', fontSize: 13 }}>Close</button>
          {!alreadyDone && (
            <button onClick={submit} disabled={loading} style={{ padding: '9px 18px', borderRadius: 9,
              border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700,
              opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Animated upvote button
function UpvoteButton({ c, onUpvote }) {
  const [anim, setAnim] = useState('');
  const [showPlus, setShowPlus] = useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    const wasVoted = c.user_has_upvoted;
    const animName = wasVoted ? 'voteUndo 0.3s ease-out' : 'votePop 0.45s cubic-bezier(0.34,1.56,0.64,1)';
    setAnim(animName);
    if (!wasVoted) {
      setShowPlus(true);
      setTimeout(() => setShowPlus(false), 650);
    }
    setTimeout(() => setAnim(''), 460);
    onUpvote(c.id);
  };

  const voted = c.user_has_upvoted;

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      {showPlus && (
        <div style={{
          position: 'absolute', inset: -4, borderRadius: '50%',
          border: `2px solid ${COLORS.purple}`,
          animation: 'voteRipple 0.5s ease-out forwards',
          pointerEvents: 'none',
        }} />
      )}
      {showPlus && (
        <span style={{
          position: 'absolute', top: -4, left: '50%',
          fontSize: 11, fontWeight: 800, color: '#c4b5fd',
          animation: 'votePlusOne 0.65s ease-out forwards',
          pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 10,
        }}>+1</span>
      )}
      <button
        onClick={handleClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '4px 10px', borderRadius: 7, cursor: 'pointer',
          fontSize: 12, fontWeight: 600,
          border: `1px solid ${voted ? COLORS.purple : 'rgba(255,255,255,0.2)'}`,
          background: voted ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.06)',
          color: voted ? '#c4b5fd' : 'rgba(255,255,255,0.6)',
          transition: 'all 0.2s',
          animation: anim || 'none',
        }}>
        {voted ? '\u25b2' : '\u25b3'} {c.upvotes || 0}
      </button>
    </div>
  );
}

const ALL_STATUSES = ['all', 'pending', 'verified', 'resolved', 'escalated'];
const STATUS_COLORS = { all: COLORS.purple, pending: COLORS.amber, verified: COLORS.blue,
  resolved: COLORS.green, escalated: COLORS.orange };

// ── Complaint Card
function ComplaintCard({ c, onClick, index, canInteract, onUpvote, onFeedback }) {
  const statusColors = { pending: COLORS.amber, verified: COLORS.blue, resolved: COLORS.green, escalated: COLORS.orange };
  const isUrgent = c.is_overdue || c.escalation_level >= 2;
  const borderColor = isUrgent ? '#ef4444' : (statusColors[c.status] || 'rgba(255,255,255,0.2)');

  return (
    <div className="ur-card-hover" style={{ padding: '16px 18px', borderRadius: 14,
      background: isUrgent ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.08)',
      border: '1px solid ' + (isUrgent ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.15)'),
      borderLeft: '3px solid ' + borderColor,
      animation: 'fadeUp 0.4s ease-out both', animationDelay: (index * 0.04) + 's',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>

      {isUrgent && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
          padding: '4px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.15)',
          border: '1px solid rgba(239,68,68,0.3)', width: 'fit-content' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444',
            boxShadow: '0 0 6px #ef4444', display: 'inline-block' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fca5a5' }}>
            {c.escalation_level >= 2 ? 'ESCALATED TO HIGHER AUTHORITY' : 'OVERDUE \u2014 URGENT'}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', flex: 1, lineHeight: 1.3, cursor: 'pointer' }}
          onClick={() => onClick(c.id)}>{c.title}</div>
        <StatusBadge status={c.status} />
      </div>

      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginBottom: 10, lineHeight: 1.5 }}>
        {c.description?.slice(0, 100)}{c.description?.length > 100 ? '...' : ''}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {c.department && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5,
          background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' }}>{c.department}</span>}
        {c.university_category && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5,
          background: 'rgba(139,92,246,0.2)', color: '#c4b5fd' }}>{c.university_category}</span>}
        {c.category && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5,
          background: 'rgba(6,182,212,0.15)', color: '#67e8f9' }}>{c.category}</span>}
      </div>

      {/* Timers */}
      {c.status === 'pending' && c.verification_deadline && (
        <CardTimer deadline={c.verification_deadline} label="Admin review" />
      )}
      {c.status === 'verified' && c.resolution_deadline && (
        <CardTimer deadline={c.resolution_deadline} label="Resolution" />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {canInteract ? (
            <UpvoteButton c={c} onUpvote={onUpvote} />
          ) : (
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>\u25b2 {c.upvotes || 0}</span>
          )}
          {canInteract && c.status === 'resolved' && onFeedback && (
            <button onClick={e => { e.stopPropagation(); onFeedback(c); }} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 7,
              cursor: 'pointer', fontSize: 12, fontWeight: 600,
              border: `1px solid ${c.feedback_rating ? COLORS.green : 'rgba(255,255,255,0.2)'}`,
              background: c.feedback_rating ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)',
              color: c.feedback_rating ? '#6ee7b7' : 'rgba(255,255,255,0.6)', transition: 'all 0.2s' }}>
              {c.feedback_rating ? '\u2605 ' + c.feedback_rating + '/5' : '\u2605 Rate'}
            </button>
          )}
        </div>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
          {c.created_at ? new Date(!c.created_at.endsWith('Z') ? c.created_at + 'Z' : c.created_at).toLocaleDateString() : ''}
        </span>
      </div>
    </div>
  );
}

// ── Status Tabs
function StatusTabs({ active, onChange, counts }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
      {ALL_STATUSES.map(s => (
        <button key={s} onClick={() => onChange(s)} style={{
          padding: '5px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
          border: `1px solid ${active === s ? STATUS_COLORS[s] : 'rgba(255,255,255,0.15)'}`,
          background: active === s ? STATUS_COLORS[s] + '33' : 'rgba(255,255,255,0.05)',
          color: active === s ? STATUS_COLORS[s] : 'rgba(255,255,255,0.6)', transition: 'all 0.2s' }}>
          {s.charAt(0).toUpperCase() + s.slice(1)} ({counts[s] || 0})
        </button>
      ))}
    </div>
  );
}

// ── Complaint Block
function ComplaintBlock({ title, icon, complaints, onClickComplaint, color, emptyText, canInteract, onUpvote, onFeedback, currentUserId }) {
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(true);

  const sorted = [...complaints].sort((a, b) => {
    const aU = (a.is_overdue || a.escalation_level >= 2) ? 1 : 0;
    const bU = (b.is_overdue || b.escalation_level >= 2) ? 1 : 0;
    if (bU !== aU) return bU - aU;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const counts = ALL_STATUSES.reduce((acc, s) => {
    acc[s] = s === 'all' ? sorted.length : sorted.filter(c => c.status === s).length;
    return acc;
  }, {});

  const filtered = filter === 'all' ? sorted : sorted.filter(c => c.status === filter);
  const shown = expanded ? filtered : filtered.slice(0, 3);
  const urgentCount = sorted.filter(c => c.is_overdue || c.escalation_level >= 2).length;

  return (
    <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.15)',
      background: 'rgba(255,255,255,0.05)', animation: 'fadeUp 0.5s ease-out both' }}>
      <div style={{ padding: '16px 20px', background: `linear-gradient(135deg, ${color}22, ${color}11)`,
        borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>{icon}</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{title}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
              {complaints.length} complaint{complaints.length !== 1 ? 's' : ''}
              {urgentCount > 0 && <span style={{ marginLeft: 8, color: '#fca5a5', fontWeight: 700 }}>{urgentCount} urgent</span>}
            </div>
          </div>
        </div>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: color + '33',
          border: `1px solid ${color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 800, color }}>{complaints.length}</div>
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <StatusTabs active={filter} onChange={setFilter} counts={counts} />
        {filtered.length === 0 ? (
          <EmptyState text={emptyText || 'No complaints'} sub="Nothing to show here yet" />
        ) : (
          <>
            {shown.map((c, i) => (
              <ComplaintCard key={c.id} c={c} onClick={onClickComplaint} index={i}
                canInteract={canInteract} onUpvote={onUpvote}
                onFeedback={c.student_id === currentUserId && c.status === 'resolved' ? onFeedback : null} />
            ))}
            {filtered.length > 3 && (
              <button onClick={() => setExpanded(e => !e)} style={{ padding: '8px', borderRadius: 8,
                cursor: 'pointer', fontWeight: 600, fontSize: 12, background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', transition: 'all 0.2s' }}>
                {expanded ? 'Show less' : `Show ${filtered.length - 3} more`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CommunityPageInner() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedbackTarget, setFeedbackTarget] = useState(null);

  useEffect(() => {
    api.get('/community/')
      .then(res => setData(res.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load community board'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingScreen />;

  const handleClick = (id) => navigate('/complaints/' + id);

  const handleUpvote = async (complaintId) => {
    try {
      const res = await api.post('/community/upvote/' + complaintId);
      const { upvotes, user_has_upvoted } = res.data;
      setData(prev => {
        const update = list => list ? list.map(c => c.id === complaintId ? { ...c, upvotes, user_has_upvoted } : c) : list;
        return { ...prev,
          dept_complaints: update(prev.dept_complaints),
          uni_complaints: update(prev.uni_complaints),
          student_complaints: update(prev.student_complaints),
          faculty_complaints: update(prev.faculty_complaints) };
      });
    } catch (e) { console.error('Upvote failed', e); }
  };

  const handleFeedbackSubmitted = (complaintId, rating, comment) => {
    setData(prev => {
      const update = list => list ? list.map(c => c.id === complaintId ? { ...c, feedback_rating: rating, feedback_comment: comment } : c) : list;
      return { ...prev,
        dept_complaints: update(prev.dept_complaints),
        uni_complaints: update(prev.uni_complaints),
        student_complaints: update(prev.student_complaints),
        faculty_complaints: update(prev.faculty_complaints) };
    });
  };

  const mode = data?.mode;
  const isAdmin = mode === 'dept_admin' || mode === 'uni_admin' || mode === 'authority';
  const canInteract = mode === 'student' || mode === 'faculty';
  const totalCount = isAdmin
    ? (data.student_complaints?.length || 0) + (data.faculty_complaints?.length || 0)
    : (data?.dept_complaints?.length || 0) + (data?.uni_complaints?.length || 0);
  const headerSubtitle = {
    student: 'All complaints from your department + university-wide',
    faculty: 'All complaints from your department + university-wide',
    dept_admin: 'All complaints in ' + (data?.department || user?.department),
    uni_admin: 'All university-wide complaints',
    authority: 'All complaints across the university',
  }[mode] || '';

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar title="Community Board" subtitle="Shared complaints feed"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={<button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/dashboard')}>Dashboard</button>} />
          <div style={s.content}>
            <PageHeader icon="&#127963;" title="Community Board" subtitle={headerSubtitle}
              stats={[{ label: 'Total Visible', value: totalCount }]} />
            {error && <div style={s.alertErr}>{error}</div>}
            {(mode === 'student' || mode === 'faculty') && data && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <ComplaintBlock title={(user?.department || '') + ' \u2014 Department Complaints'} icon="&#127963;"
                  complaints={data.dept_complaints || []} onClickComplaint={handleClick}
                  color={COLORS.blue} emptyText="No department complaints yet"
                  canInteract={canInteract} onUpvote={handleUpvote} onFeedback={setFeedbackTarget} currentUserId={user?.id} />
                <ComplaintBlock title="University-Wide Complaints" icon="&#127891;"
                  complaints={data.uni_complaints || []} onClickComplaint={handleClick}
                  color={COLORS.purple} emptyText="No university-wide complaints yet"
                  canInteract={canInteract} onUpvote={handleUpvote} onFeedback={setFeedbackTarget} currentUserId={user?.id} />
              </div>
            )}
            {isAdmin && data && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(420px,1fr))', gap: 20 }}>
                <ComplaintBlock title="Student Complaints" icon="&#127890;"
                  complaints={data.student_complaints || []} onClickComplaint={handleClick}
                  color={COLORS.cyan} emptyText="No student complaints"
                  canInteract={false} onUpvote={handleUpvote} currentUserId={user?.id} />
                <ComplaintBlock title="Faculty Complaints" icon="&#128104;&#8205;&#127979;"
                  complaints={data.faculty_complaints || []} onClickComplaint={handleClick}
                  color={COLORS.amber} emptyText="No faculty complaints"
                  canInteract={false} onUpvote={handleUpvote} currentUserId={user?.id} />
              </div>
            )}
          </div>
        </div>
      </div>
      {feedbackTarget && (
        <FeedbackModal complaint={feedbackTarget} onClose={() => setFeedbackTarget(null)}
          onSubmitted={handleFeedbackSubmitted} />
      )}
    </div>
  );
}

export default function CommunityPage() {
  return <ThemeProvider><CommunityPageInner /></ThemeProvider>;
}
